import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { database, realtimeDatabase } from "../../database/firebase";
import { ref, onValue } from 'firebase/database';
import './TournamentStructure.css';

const TS_LIGHT_SURFACE = 'rgba(255, 255, 255, 0.94)';

function parseRgbFromCssColor(cssColor) {
  if (!cssColor || typeof cssColor !== 'string') return null;
  const c = cssColor.trim().toLowerCase();
  if (c === 'black' || c === '#000' || c === '#000000') return { r: 0, g: 0, b: 0 };
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  const rgb = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return { r: +rgb[1], g: +rgb[2], b: +rgb[3] };
  return null;
}

function isSurfaceTooDark(cssColor) {
  const rgb = parseRgbFromCssColor(cssColor);
  if (!rgb) return false;
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum < 0.28;
}

function surfaceBackgroundFromTheme(boxcolor) {
  if (boxcolor == null || String(boxcolor).trim() === '') return null;
  const t = String(boxcolor).trim();
  if (isSurfaceTooDark(t)) return TS_LIGHT_SURFACE;
  return t;
}

function textLuminance(cssColor) {
  const rgb = parseRgbFromCssColor(cssColor);
  if (!rgb) return null;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

const TS_SWAL_BTN_DEFAULT = '#1548a0';
const TS_SWAL_BTN_TEXT_DEFAULT = '#ffffff';

function fireStructureAlert({
  variant,
  title,
  text,
  confirmButtonText = 'OK',
  buttonBg,
  buttonText
}) {
  const success = variant === 'success';
  const bg = buttonBg || (success ? TS_SWAL_BTN_DEFAULT : '#dc2626');
  const fg = buttonText || TS_SWAL_BTN_TEXT_DEFAULT;

  return Swal.fire({
    icon: success ? 'success' : 'error',
    iconColor: success ? '#16a34a' : '#f87171',
    title,
    text,
    confirmButtonText,
    buttonsStyling: false,
    width: '26.5rem',
    padding: 0,
    backdrop: 'rgba(15, 23, 42, 0.55)',
    allowOutsideClick: true,
    customClass: {
      popup: `ts-swal-popup${success ? ' ts-swal-popup--success' : ' ts-swal-popup--error'}`,
      title: 'ts-swal-title',
      htmlContainer: 'ts-swal-html',
      confirmButton: 'ts-swal-btn',
      actions: 'ts-swal-actions',
      icon: 'ts-swal-icon-wrap'
    },
    didOpen: (popup) => {
      const btn = popup.querySelector('.ts-swal-btn');
      if (btn) {
        btn.style.backgroundColor = bg;
        btn.style.color = fg;
        btn.style.border = 'none';
      }
    }
  });
}

const TournamentStructure = ({ updateHeader }) => {
  const navigate = useNavigate();
  const [themeData, setThemeData] = useState(null);
  const [searchParams] = useSearchParams();
  const tournamentId = searchParams.get('tournamentId');
  const [tournamentData, setTournamentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState(0);
  const [apiRounds, setApiRounds] = useState([]);
  const [userData, setUserData] = useState(null);
  const [textcolor, Setcolorcode] = useState(null);
  const [boxcolor, SetBoxcolorcode] = useState(null);
  const [boxtextcolor, SetBoxTextcolor] = useState(null);
  const [Buttoncolor, SetButtoncolor] = useState(null);
  const [buttontextcolor, SetButtonTextcolor] = useState(null);
  const [riddlerusLogo, SetriddlerusLogo] = useState("/img/logo.png");
  const [currentPage, setCurrentPage] = useState(1);
  const [matchesPerPage] = useState(10);
  const [firebaseGameMap, setFirebaseGameMap] = useState({});
  const intervalRef = useRef(null);

  useEffect(() => {
    if (themeData != null) {
      const body = document.body;

      if (body.classList.contains('common-bg')) {
        // Check if it's a mobile device (or you can define your own breakpoint)
        if (window.innerWidth <= 768) {
          // Apply mobile background
          console.log(themeData.background_mob);
          body.style.backgroundImage = `url(${themeData.background_mob})`;
        } else {
          // Apply desktop background
          console.log(themeData.background_desk);
          body.style.backgroundImage = `url(${themeData.background_desk})`;
        }
      }
      SetriddlerusLogo(themeData.logo);
    }
  }, [themeData]);

  useEffect(() => {
    const storedSession = sessionStorage.getItem("userData");
    if (storedSession) {
      const parsedUser = JSON.parse(storedSession);
      const userWithTournament = { ...parsedUser, tournamentId, gametype: 'tournament' };
      setUserData(userWithTournament);
      sessionStorage.setItem("userData", JSON.stringify(userWithTournament));
      fetchTournamentData(userWithTournament, true);
    } else {
      navigate("/login");
    }
  }, [tournamentId, navigate]);

  // Reset current page when active round changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeRound]);

  useEffect(() => {
    updateHeader({
      backButtonRedirect: "/tournament",
      backButtonDisabled: false
    });

    const fetchData = async () => {
      const storedSession = sessionStorage.getItem("userData");
      console.log(storedSession);
      const storedTheme = sessionStorage.getItem("themeData");

      if (storedSession && storedTheme) {
        const parsedUser = JSON.parse(storedSession);
        const parsedTheme = JSON.parse(storedTheme);
        const userWithTournament = { ...parsedUser, tournamentId };
        setUserData(userWithTournament);
        sessionStorage.setItem("userData", JSON.stringify(userWithTournament));
        setThemeData(parsedTheme);
        Setcolorcode(parsedTheme.landing_page_title_color);
        SetBoxcolorcode(parsedTheme.box_color);
        SetBoxTextcolor(parsedTheme.box_text_color);
        SetButtonTextcolor(parsedTheme.button_text_color);
        SetButtoncolor(parsedTheme.button_color);
      } else {
        navigate("/login"); // Redirect if session data is missing
      }
    };

    fetchData();
  }, [database, navigate]);

  const fetchTournamentData = async (user, showLoading = true) => {
    const tid = user?.tournamentId ?? tournamentId;
    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_PHP_ADMIN_BASE_URL}getMatchesList.php`,
        {
          tournamentId: tid,
          userId: user.userId
        },
        {
          headers: {
            'Content-Type': 'text/plain'
          }
        }
      );

      if (response.data && response.data.tournaments) {
        //  console.log("getMatchesList----", response.data);
        setTournamentData(response.data);
        setApiRounds(response.data.tournaments || []);
        if (showLoading) {
          // Find current round (first round with not_started matches)
          const currentRoundIndex = response.data.tournaments.findIndex((round) =>
            round.matches.some((match) => match.status === 'not_started')
          );
          setActiveRound(currentRoundIndex >= 0 ? currentRoundIndex : 0);
        }
      } else {
        fireStructureAlert({
          variant: 'error',
          title: 'Couldn’t load bracket',
          text: response.data?.error || 'Tournament data is unavailable.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error fetching tournament data:', error);
      fireStructureAlert({
        variant: 'error',
        title: 'Couldn’t load bracket',
        text: 'Please check your connection and try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!realtimeDatabase || !apiRounds?.length) return undefined;

    const gameIds = new Set();
    apiRounds.forEach((round) => {
      (round.matches || []).forEach((match) => {
        if (!match?.gameId || match.is_bye) return;
        if (match.status === 'completed' || match.outcome === 'win' || match.outcome === 'winner') return;
        gameIds.add(String(match.gameId));
      });
    });

    const unsubscribers = [];
    gameIds.forEach((gameId) => {
      const gameRef = ref(realtimeDatabase, `games/${gameId}`);
      const unsubscribe = onValue(gameRef, (snapshot) => {
        const data = snapshot.val();
        setFirebaseGameMap((prev) => {
          const next = { ...prev };
          if (!data) {
            delete next[gameId];
            return next;
          }

          const players = data.players || {};
          const playerCount = Object.values(players).filter((player) => player != null).length;
          const requiredPlayers = data.gameMode === '2v2' ? 4 : 2;

          next[gameId] = {
            status: data.status || '',
            playerCount,
            isPlaying: data.status === 'playing' && playerCount >= requiredPlayers,
          };
          return next;
        });
      });
      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [apiRounds]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const storedSession = sessionStorage.getItem('userData');
      if (!storedSession || !tournamentId) return;
      const parsedUser = JSON.parse(storedSession);
      const userWithTournament = { ...parsedUser, tournamentId, gametype: 'tournament' };
      fetchTournamentData(userWithTournament, false);
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [tournamentId]);

  // ---- Elimination-style round helpers (from TournamentStructure copy) ----
  const getEliminationRoundCount = (participantCount) =>
    Math.max(1, Math.ceil(Math.log2(participantCount)));

  const DEFAULT_PARTICIPANT_COUNT = 8;

  // Derive round tabs from the latest non-empty round coming from API.
  // This handles irregular brackets (BYE / abandonment) better than using Round-1 match count.
  const roundCount = useMemo(() => {
    if (!apiRounds?.length) return getEliminationRoundCount(DEFAULT_PARTICIPANT_COUNT);

    let latestIdx = -1;
    for (let i = apiRounds.length - 1; i >= 0; i--) {
      if (apiRounds[i]?.matches?.length) {
        latestIdx = i;
        break;
      }
    }
    if (latestIdx === -1) return getEliminationRoundCount(DEFAULT_PARTICIPANT_COUNT);

    const latestMatchCount = apiRounds[latestIdx].matches.length;
    // If latest has M matches, remaining rounds from this round to FINAL is ceil(log2(M)) + 1
    const remainingRounds = Math.max(
      1,
      Math.ceil(Math.log2(Math.max(1, latestMatchCount))) + 1
    );

    return latestIdx + remainingRounds;
  }, [apiRounds]);

  const rounds = useMemo(
    () =>
      Array.from({ length: roundCount }, (_, i) => ({
        roundIndex: i,
        matches: apiRounds[i]?.matches ?? [],
      })),
    [roundCount, apiRounds]
  );

  const getRoundName = (index, total) => {
    if (total - index === 1) return 'FINAL';
    if (total - index === 2) return 'SEMI FINAL';
    return `Round ${index + 1}`;
  };

  // Placeholder count when API returns no matches: final=1, semi=2, others=1
  const getTbdCount = (roundIndex, totalRounds) => {
    if (totalRounds - roundIndex === 1) return 1; // FINAL
    if (totalRounds - roundIndex === 2) return 2; // SEMI FINAL
    return 1; // other rounds
  };

  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    const formattedDate = date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${formattedDate} ${formattedTime}`;
  };

  const isTimeAllowed = (matchTime) => {
    const matchStartTime = new Date(matchTime);
    const matchEndTime = new Date(matchStartTime);
    matchEndTime.setMinutes(matchEndTime.getMinutes() + 40);
    const currentTime = new Date();

    return currentTime >= matchStartTime && currentTime <= matchEndTime;
  };

  const isTimePassed = (matchTime) => {
    const matchStartTime = new Date(matchTime);
    const matchEndTime = new Date(matchStartTime);
    matchEndTime.setMinutes(matchEndTime.getMinutes() + 40);
    const currentTime = new Date();

    return currentTime >= matchEndTime;
  };

  const isMatchPlayingOnFirebase = (match) => {
    if (!match?.gameId) return false;
    const live = firebaseGameMap[String(match.gameId)];
    return Boolean(live?.isPlaying);
  };

  const renderButton = (match, roundIndex = null, totalRounds = null) => {
    if (match.is_bye) {
      return null;
    }

    // Check if outcome is "winner" or "win" and show "completed" - this takes priority over everything
    if (match.outcome === 'winner' || match.outcome === 'win' || match.outcome === 'abandonment') {
      return <span className="ts-pill ts-pill--done">Completed</span>;
    }

    const gameUrl = (spectate = false) => {
      const params = new URLSearchParams({ gameId: match.gameId });
      if (roundIndex != null && totalRounds != null) {
        params.set('round', String(roundIndex + 1));
        params.set('roundName', getRoundName(roundIndex, totalRounds));
      }
      if (spectate) {
        params.set('spectate', '1');
      }
      return `/game?${params.toString()}`;
    };

    // Live match: a player of this match (canJoin) can still Play (join/rejoin);
    // non-players get a "View" button to spectate the live match (read-only).
    if (isMatchPlayingOnFirebase(match)) {
      if (match.canJoin) {
        return (
          <button type="button" className="ts-btn ts-btn--primary" onClick={() => navigate(gameUrl())}>
            Play
          </button>
        );
      }
      return (
        <button type="button" className="ts-btn ts-btn--view" onClick={() => navigate(gameUrl(true))}>
          View
        </button>
      );
    }

    const timeAllowed = isTimeAllowed(match.datetime);
    const timePassed = isTimePassed(match.datetime);

    if (match.canJoin) {
      if (timeAllowed) {
        return (
          <button type="button" className="ts-btn ts-btn--primary" onClick={() => navigate(gameUrl())}>
            Play
          </button>
        );
      } else {
        return (
          <button type="button" className="ts-btn ts-btn--disabled" disabled>
            {timePassed && (!match.outcome || match.outcome === '') ? 'Access closed' : 'Play'}
          </button>
        );
      }
    }

    if (match.status === 'completed') {
      return <span className="ts-pill ts-pill--done">Completed</span>;
    }

    if (match.status === 'not_started') {
      return <span className="ts-pill ts-pill--pending">Not started</span>;
    }

    return <span className="ts-pill ts-pill--muted">{match.status}</span>;
  };

  const getWinner = (match) => {
    if (match.status !== 'completed' || match.is_bye) {
      return '';
    }

    if (match.outcome === 'draw' ||
      match.outcome === 'draw due to fifty-move rule' ||
      match.outcome === 'draw due to insufficient material' ||
      match.outcome === 'double forfeit') {
      return '';
    }

    // Derive winner's display name from match + players
    const winnerId = match.winnerId || match.winner_id || match.winner || null;
    const winnerNameFromMatch = match.winnerName || match.winner_name || null;

    if (Array.isArray(match.players) && match.players.length > 0) {
      // Try to find winner among players by id or color or name
      const winnerPlayer = match.players.find((p) => {
        if (!p) return false;
        const pid =
          p.userId ||
          p.userid ||
          p.id ||
          p.playerId ||
          p.player_id ||
          null;

        const pColor = p.color || p.playerColor || null;

        return (
          (winnerId && pid && String(winnerId) === String(pid)) ||
          (winnerId && pColor && String(winnerId).toLowerCase() === String(pColor).toLowerCase()) ||
          (winnerNameFromMatch && p.name && winnerNameFromMatch === p.name)
        );
      });

      if (winnerPlayer && winnerPlayer.name) {
        return `${winnerPlayer.name} (WINNER)`;
      }
    }

    // Fallback: show winnerId or name if available
    if (winnerNameFromMatch) return `${winnerNameFromMatch} (WINNER)`;
    if (winnerId) return `${winnerId} (WINNER)`;

    return '';
  };

  const isPlayerWinner = (match, playerIndex) => {
    if (match.status !== 'completed' || match.is_bye) return false;

    if (
      match.outcome === 'draw' ||
      match.outcome === 'draw due to fifty-move rule' ||
      match.outcome === 'draw due to insufficient material' ||
      match.outcome === 'double forfeit'
    ) {
      return false;
    }

    const players = match.players || [];
    const player = players[playerIndex];
    if (!player) return false;

    const winnerId = match.winnerId || match.winner_id || match.winner || null;
    const winnerNameFromMatch = match.winnerName || match.winner_name || null;

    const pid =
      player.userId ||
      player.userid ||
      player.id ||
      player.playerId ||
      player.player_id ||
      null;

    const pColor = player.color || player.playerColor || null;

    if (winnerId && pid && String(winnerId) === String(pid)) return true;
    if (
      winnerId &&
      pColor &&
      String(winnerId).toLowerCase() === String(pColor).toLowerCase()
    ) {
      return true;
    }
    if (winnerNameFromMatch && player.name && winnerNameFromMatch === player.name) {
      return true;
    }

    return false;
  };

  const getOutcome = (match) => {
    if (match.is_bye) {
      return 'BYE';
    }

    const raw =
      match.outcome ??
      match.match_outcome ??
      match.matchOutcome ??
      match.result ??
      match.matchResult;
    let text =
      raw != null && raw !== ''
        ? String(raw).trim()
        : '';

    // DB often has winnerId + status completed but empty outcome (e.g. save-winner path)
    if (!text && match.status === 'completed') {
      const wid = match.winnerId ?? match.winner_id ?? match.winner;
      const hasWinner = wid != null && String(wid).trim() !== '';
      if (hasWinner) {
        text = 'Win';
      }
    }

    if (!text) {
      return '—';
    }

    const lower = text.toLowerCase();
    if (lower === 'win' || lower === 'winner') {
      text = 'Win';
    }

    if (match.game_type === 'armageddon') {
      text += '\n(armageddon)';
    }
    return text;
  };

  const groupPlayersByColor = (players) => {
    if (!players || !Array.isArray(players)) return { team1: [], team2: [] };
    
    const teams = { team1: [], team2: [] };
    const colorGroups = {};
    
    // Group players by color
    players.forEach(player => {
      const color = player.color || player.playerColor || 'unknown';
      if (!colorGroups[color]) {
        colorGroups[color] = [];
      }
      colorGroups[color].push(player);
    });
    
    // Assign color groups to teams
    const colors = Object.keys(colorGroups);
    if (colors.length >= 2) {
      teams.team1 = colorGroups[colors[0]] || [];
      teams.team2 = colorGroups[colors[1]] || [];
    } else if (colors.length === 1) {
      teams.team1 = colorGroups[colors[0]] || [];
      teams.team2 = [];
    }
    
    return teams;
  };

  // Pagination helpers
  const getPaginatedMatches = (matches) => {
    const indexOfLastMatch = currentPage * matchesPerPage;
    const indexOfFirstMatch = indexOfLastMatch - matchesPerPage;
    return matches.slice(indexOfFirstMatch, indexOfLastMatch);
  };

  const getTotalPages = (matches) => {
    return Math.ceil(matches.length / matchesPerPage);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = (matches) => {
    const totalPages = getTotalPages(matches);
    if (totalPages <= 1) return null;

    return (
      <nav className="ts-pagination" aria-label="Match pages">
        <button
          type="button"
          className="ts-page-nav"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
          <button
            key={pageNumber}
            type="button"
            className={`ts-page-num${currentPage === pageNumber ? ' ts-page-num--active' : ''}`}
            onClick={() => handlePageChange(pageNumber)}
          >
            {pageNumber}
          </button>
        ))}
        <button
          type="button"
          className="ts-page-nav"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </nav>
    );
  };

  const themeStyle = useMemo(() => {
    const s = {};
    if (textcolor) s['--ts-title'] = textcolor;
    const safeCardBg = surfaceBackgroundFromTheme(boxcolor);
    if (safeCardBg) s['--ts-card-bg'] = safeCardBg;
    if (boxtextcolor) {
      const usedLightFallback =
        Boolean(boxcolor && String(boxcolor).trim()) &&
        safeCardBg === TS_LIGHT_SURFACE &&
        isSurfaceTooDark(String(boxcolor).trim());
      const tlum = textLuminance(boxtextcolor);
      if (!(usedLightFallback && tlum != null && tlum > 0.62)) {
        s['--ts-card-text'] = boxtextcolor;
      }
    }
    if (Buttoncolor) s['--ts-btn-bg'] = Buttoncolor;
    if (buttontextcolor) s['--ts-btn-text'] = buttontextcolor;
    return s;
  }, [textcolor, boxcolor, boxtextcolor, Buttoncolor, buttontextcolor]);

  const textColorStyle = textcolor ? { color: textcolor } : undefined;

  const renderTeamPlayers = (team, match, teamIndex) => {
    if (!team || team.length === 0) return '---';
    
    return (
      <div className="ts-player-line">
        {team.map((player, index) => (
          <span key={index}>
            {player.name}
            {isPlayerWinner(match, player.userId || player.userid || player.id) && ' (WINNER)'}
            {index < team.length - 1 && <span> & </span>}
          </span>
        ))}
      </div>
    );
  };

  const is2v2Match = (match) => {
    return match.match_type === '2v2' || match.game_type === '2v2';
  };

  const getUserParticipationRole = () => {
    const currentUserId = userData?.userId;
    if (!currentUserId) return 'Watcher';

    const matches = rounds[activeRound]?.matches || [];
    const isParticipant = matches.some((match) => {
      if (!match || match.is_bye || !Array.isArray(match.players)) return false;
      return match.players.some((player) => {
        if (!player) return false;
        const pid = player.userId || player.userid || player.id || player.playerId || player.player_id || null;
        return pid != null && String(pid) === String(currentUserId);
      });
    });

    return isParticipant ? 'Player' : 'Watcher';
  };

  if (loading) {
    return (
      <div className="ts-page" style={themeStyle}>
        <div className="ts-loading">
          <div className="ts-loading__spinner" aria-hidden />
          <span style={textColorStyle}>Loading bracket…</span>
        </div>
      </div>
    );
  }

  if (!tournamentData || !tournamentData.tournaments) {
    return (
      <div className="ts-page" style={themeStyle}>
        <div className="ts-empty ts-empty--error" role="alert">
          <strong style={textColorStyle}>No bracket data</strong>
          <span style={textColorStyle}>This tournament may not be available.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ts-page" style={themeStyle}>
      <header className="ts-header">
        <h1 className="ts-title" style={textColorStyle}>
          Tournament schedule
        </h1>
        {/* <p className="ts-subtitle" style={textColorStyle}>
          Pick a round, then join your match when the time window is open.
        </p> */}
        <p className="ts-participation-note" style={textColorStyle}>
          You are currently participating as: {getUserParticipationRole()}. Your match will automatically become active at the scheduled start time shown above. If it does not activate exactly at the scheduled time, please refresh the match page. If you join after the scheduled start time or after the match has ended, you will be marked as a No Show, and only the View button will be available. If your opponent does not join, you must remain on the match page until the scheduled end of the match. Leaving before the match concludes may affect the result. If your opponent fails to join for the entire match duration and you remain present until the end, you will be declared the winner.
        </p>
      </header>

      <div className="ts-tabs">
        <div className="ts-tabs-track" role="tablist" aria-label="Rounds">
          {rounds.map((_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={activeRound === index}
              className={`ts-tab${activeRound === index ? ' ts-tab--active' : ''}`}
              onClick={() => setActiveRound(index)}
            >
              {getRoundName(index, rounds.length)}
            </button>
          ))}
        </div>
      </div>

      {rounds.map((round, roundIndex) => (
        <div
          key={roundIndex}
          className={`ts-panel${activeRound === roundIndex ? '' : ' ts-panel--hidden'}`}
          role="tabpanel"
          hidden={activeRound !== roundIndex}
        >
          <div className="ts-round-banner">
            <span className="ts-round-banner__label">{getRoundName(roundIndex, rounds.length)}</span>
          </div>

          <div className="ts-table-scroll">
            <table className="ts-table">
              <thead>
                <tr>
                  <th className="ts-table__th ts-table__th--hide">ID</th>
                  <th className="ts-table__th ts-table__th--hide">Next ID</th>
                  <th scope="colgroup" colSpan={3} className="ts-table__th ts-table__th--matchup">
                    {'Player & opponent'}
                  </th>
                  <th scope="col" className="ts-table__th">
                    Outcome
                  </th>
                  <th scope="col" className="ts-table__th">
                    {'Date & time'}
                  </th>
                  <th scope="col" className="ts-table__th ts-table__th--action">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {(round.matches.length > 0
                  ? getPaginatedMatches(round.matches)
                  : getPaginatedMatches(Array(getTbdCount(roundIndex, rounds.length)).fill({ tbd: true }))
                ).map((match, matchIndex) => {
                  if (match.tbd) {
                    return (
                      <tr key={matchIndex}>
                        <td className="ts-table__td ts-table__th--hide">-</td>
                        <td className="ts-table__td ts-table__th--hide">-</td>
                        <td className="ts-table__td ts-table__td--name" data-label="Player 1">TBD</td>
                        <td className="ts-table__td ts-table__td--vs" data-label="">
                          <img src="/img/vs.png" className="ts-vs" alt="" />
                        </td>
                        <td className="ts-table__td ts-table__td--name" data-label="Player 2">TBD</td>
                        <td className="ts-table__td ts-table__td--outcome" data-label="Outcome">—</td>
                        <td className="ts-table__td ts-table__td--datetime" data-label="Date & Time">—</td>
                        <td className="ts-table__td ts-table__td--action" data-label="Status">—</td>
                      </tr>
                    );
                  }

                  const canJoin = match.canJoin;
                  const is2v2 = is2v2Match(match);

                  return (
                    <tr key={matchIndex} data-canjoin={canJoin ? 'true' : 'false'}>
                      <td className="ts-table__td ts-table__th--hide">{match.matchId}</td>
                      <td className="ts-table__td ts-table__th--hide">{match.nextMatchId || ''}</td>
                      <td className="ts-table__td ts-table__td--name" data-label="Player 1">
                        {is2v2 ? (
                          renderTeamPlayers(groupPlayersByColor(match.players).team1, match, 0)
                        ) : (
                          <>
                            {match.players && match.players[0] ? match.players[0].name : '—'}
                            {match.players && match.players[0] && isPlayerWinner(match, 0) && (
                              <span className="ts-winner-tag">Winner</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="ts-table__td ts-table__td--vs" data-label="">
                        <img src="/img/vs.png" className="ts-vs" alt="" />
                      </td>
                      <td className="ts-table__td ts-table__td--name" data-label="Player 2">
                        {is2v2 ? (
                          renderTeamPlayers(groupPlayersByColor(match.players).team2, match, 1)
                        ) : (
                          <>
                            {match.players && match.players[1] ? match.players[1].name : '—'}
                            {match.players && match.players[1] && isPlayerWinner(match, 1) && (
                              <span className="ts-winner-tag">Winner</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="ts-table__td ts-table__td--outcome" data-label="Outcome">{getOutcome(match)}</td>
                      <td className="ts-table__td ts-table__td--datetime" data-label="Date & Time">
                        {match.datetime ? formatDateTime(match.datetime) : ''}
                      </td>
                      <td className="ts-table__td ts-table__td--action" data-label="Status">
                        {renderButton(match, roundIndex, rounds.length)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      
      {renderPagination(rounds[activeRound]?.matches.length > 0 ? rounds[activeRound].matches : Array(getTbdCount(activeRound, rounds.length)).fill({ tbd: true }))}
      {/* 
      <div className="decorative-images">
        <img src="images/key1.png" className="top-left-image" alt="Key decoration" />
        <img src="images/key2.png" className="top-right-image" alt="Key decoration" />
      </div> */}
    </div>
  );
};

export default TournamentStructure;
